import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  Play, Download, Trash2, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Film, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const OVERALL = {
  COMPLIANT: { icon: CheckCircle2, cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", label: "Compliant" },
  REVIEW: { icon: AlertTriangle, cls: "bg-amber-500/15 text-amber-400 border-amber-500/30", label: "Needs Review" },
  FAILED: { icon: XCircle, cls: "bg-red-500/15 text-red-400 border-red-500/30", label: "Failed" },
};

const CHECK_CLS = { PASS: "text-emerald-400", WARNING: "text-amber-400", FAIL: "text-red-400" };

export function ClipCard({ clip, onChange }) {
  const [thumb, setThumb] = useState(null);
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const comp = clip.compliance || { overall: "REVIEW", checks: [], score: 0 };
  const O = OVERALL[comp.overall] || OVERALL.REVIEW;

  useEffect(() => {
    let url;
    api.get(`/clips/${clip.id}/thumb`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setThumb(url); })
      .catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [clip.id]);

  const openPreview = async () => {
    setOpen(true);
    if (!preview) {
      try {
        const r = await api.get(`/clips/${clip.id}/stream`, { responseType: "blob" });
        setPreview(URL.createObjectURL(r.data));
      } catch {
        toast.error("Preview unavailable");
      }
    }
  };

  const download = async () => {
    try {
      const r = await api.get(`/clips/${clip.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${clip.title || "clip"}.mp4`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const remove = async () => {
    await api.delete(`/clips/${clip.id}`);
    toast.success("Clip deleted");
    onChange && onChange();
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      await api.post(`/clips/${clip.id}/regenerate`);
      toast.success("Regenerating clip…");
      setTimeout(() => onChange && onChange(), 4000);
    } catch {
      toast.error("Regenerate failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#141419] border border-white/10 rounded-2xl overflow-hidden hover:border-amber-500/30 transition-colors" data-testid={`clip-card-${clip.id}`}>
      <div className="relative aspect-video bg-zinc-900 flex items-center justify-center cursor-pointer group" onClick={openPreview}>
        {thumb ? <img src={thumb} alt={clip.title} className="w-full h-full object-cover" /> : <Film className="w-8 h-8 text-zinc-700" />}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Play className="w-10 h-10 text-white" />
        </div>
        <span className="absolute bottom-2 right-2 text-[10px] font-mono bg-black/70 px-1.5 py-0.5 rounded">{Math.round(clip.duration)}s · {clip.aspect_ratio}</span>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-semibold text-sm leading-tight truncate">{clip.title}</h3>
          <Badge className={`${O.cls} border text-[10px] shrink-0 flex items-center gap-1`}><O.icon className="w-3 h-3" /> {O.label}</Badge>
        </div>
        {clip.campaign_name && <p className="text-xs text-zinc-500 mb-2 truncate">{clip.campaign_name}</p>}
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-3.5 h-3.5 text-zinc-500" />
          <span className="text-xs text-zinc-400">Compliance {comp.score}%</span>
          {clip.score != null && <span className="text-xs text-zinc-600">· AI {clip.score}</span>}
        </div>
        <div className="flex gap-2">
          <Button onClick={download} data-testid={`download-clip-${clip.id}`} className="flex-1 h-8 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold text-xs"><Download className="w-3.5 h-3.5 mr-1" /> Download</Button>
          <Button onClick={regenerate} disabled={busy} variant="outline" className="h-8 w-8 p-0 border-white/10" title="Regenerate"><RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /></Button>
          <Button onClick={remove} variant="outline" className="h-8 w-8 p-0 border-white/10 hover:text-red-400" title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-3xl">
          <DialogHeader><DialogTitle className="font-display">{clip.title}</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-black rounded-xl overflow-hidden flex items-center justify-center">
              {preview ? <video src={preview} controls autoPlay className="w-full max-h-[60vh]" data-testid="clip-preview-video" /> : <div className="p-10"><Film className="w-10 h-10 text-zinc-700" /></div>}
            </div>
            <div>
              <h4 className="font-display font-semibold mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-amber-400" /> Compliance Report</h4>
              <div className="space-y-2 mb-4">
                {comp.checks.map((ck, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-zinc-300">{ck.name}</span>
                    <span className={`font-mono text-xs ${CHECK_CLS[ck.status]}`}>{ck.status}</span>
                  </div>
                ))}
              </div>
              <div className={`rounded-lg p-3 text-center ${O.cls} border`}>
                <O.icon className="w-5 h-5 mx-auto mb-1" />
                <p className="font-display font-bold text-sm">{O.label} · {comp.score}%</p>
              </div>
              <Button onClick={download} className="w-full mt-4 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold"><Download className="w-4 h-4 mr-1.5" /> Download Clip</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
